import React, { useEffect } from 'react';
import SEOMetaTags from '../components/SEOMetaTags';
import Header from '../components/Header';
import { trackPageView } from '../analytics';

export default function NotFound() {
  useEffect(() => {
    trackPageView('/404', 'Page Not Found');
  }, []);

  return (
    <>
      <SEOMetaTags 
        title="404 - Page Not Found | Temp2Doc PDF Tools"
        description="The page you're looking for doesn't exist. Explore our free online PDF tools including converters, mergers, splitters, and AI chat."
        keywords="404, page not found, PDF tools"
        canonicalUrl="https://temp2doc.com/404"
        robots="noindex, nofollow"
      />
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-white to-blue-50 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block p-6 bg-red-100 rounded-full mb-6">
            <svg className="w-24 h-24 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">404</h1>
          <h2 className="text-3xl font-semibold text-gray-800 mb-4">Page Not Found</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            The page you're looking for doesn't exist or has been moved. Let's get you back to our PDF tools.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <a 
              href="/" 
              className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Go Home
            </a>
          </div>

          {/* All PDF Tools */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">All PDF Tools</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <a href="/chat-with-pdf" className="bg-white rounded-lg p-5 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Chat with PDF</h4>
                <p className="text-gray-600 text-sm">Ask questions and get instant answers</p>
              </a>
              <a href="/merge-pdf" className="bg-white rounded-lg p-5 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Merge PDF</h4>
                <p className="text-gray-600 text-sm">Combine multiple PDF files into one</p>
              </a>
              <a href="/split-remove-rotate-pdf" className="bg-white rounded-lg p-5 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Split, Remove & Rotate</h4>
                <p className="text-gray-600 text-sm">Split, remove pages, or rotate PDFs</p>
              </a>
              <a href="/pdf-to-word" className="bg-white rounded-lg p-5 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">PDF to Word</h4>
                <p className="text-gray-600 text-sm">Convert PDFs to editable Word documents</p>
              </a>
              <a href="/pdf-to-excel" className="bg-white rounded-lg p-5 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">PDF to Excel</h4>
                <p className="text-gray-600 text-sm">Extract tables to Excel spreadsheets</p>
              </a>
              <a href="/pdf-to-image" className="bg-white rounded-lg p-5 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">PDF to Image</h4>
                <p className="text-gray-600 text-sm">Convert PDF pages to images</p>
              </a>
              <a href="/pdf-to-powerpoint" className="bg-white rounded-lg p-5 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">PDF to PowerPoint</h4>
                <p className="text-gray-600 text-sm">Convert PDFs to editable presentations</p>
              </a>
              <a href="/protect-pdf" className="bg-white rounded-lg p-5 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Protect PDF</h4>
                <p className="text-gray-600 text-sm">Add password protection to PDFs</p>
              </a>
              <a href="/unlock-pdf" className="bg-white rounded-lg p-5 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Unlock PDF</h4>
                <p className="text-gray-600 text-sm">Remove password protection from PDFs</p>
              </a>
              <a href="/transform-pdf" className="bg-white rounded-lg p-5 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Transform PDF</h4>
                <p className="text-gray-600 text-sm">Resize, crop, and transform pages</p>
              </a>
              <a href="/compress-pdf" className="bg-white rounded-lg p-5 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Compress PDF</h4>
                <p className="text-gray-600 text-sm">Reduce PDF file size efficiently</p>
              </a>
              <a href="/diagnose-pdf" className="bg-white rounded-lg p-5 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Diagnose PDF</h4>
                <p className="text-gray-600 text-sm">Validate and check PDF file integrity</p>
              </a>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
